/**
 * Pure helpers that answer specific yes/no questions about the shop's
 * current setup, so each component can ask only what it needs without
 * depending on a dashboard-wide "lifecycle" abstraction.
 *
 * None of these helpers fetch data — they take whatever the consumer
 * already has from `useShop()` / `useInventorySources()` / `useChannels()`.
 * That's deliberate: each consumer decides which of those hooks to call,
 * so a small surface like the AppSidebar doesn't have to load orders just
 * to render an "+ LEARN MORE" pill.
 *
 * For plan / billing / trial-driven affordances see `useTrialStatus()` in
 * this file and `docs/billing-data-requirements.md`.
 */

import { useShop } from '@/hooks/useShop';
import { useBillingStatus } from '@/hooks/useBillingStatus';

const TRIAL_DAYS_FALLBACK = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * A shop has "GBP linked" iff either:
 *   - the gmbConnection has reached the `connected` status (the Unify-side
 *     check used in `useGoogleBusinessProfile`/`useGBPConnectionPolling`), OR
 *   - the shop already has a `googleMerchantId` (the fallback retailers-dashboard
 *     uses — see `interfaces/services/retailers-dashboard/src/pages/onboarding/GMBConnect/index.jsx`).
 *
 * Mirroring both branches keeps Unify's behaviour aligned with how the rest of
 * the platform decides whether the GBP step is done.
 */
const hasGbpLinked = (shop) =>
	Boolean(shop?.gmbConnection?.status === 'connected' || shop?.gmbConnection?.accountId || shop?.googleMerchantId || shop?.googlePlaceId);

/**
 * A shop "has ever ingested" iff one of its inventory sources carries a
 * `latestIngest`. The top-level `latestIngest` is intentionally stripped from
 * the `/api/my-shop` response by `allowed-attributes.js`; the same data is
 * supplemented back in via `stockInventorySource` / `productInventorySource`
 * by `formatInventorySource()` in `get-my-shops.js`. Read it from there so
 * we don't have to widen the public attribute list.
 */
const hasEverIngested = (shop) => Boolean(shop?.stockInventorySource?.latestIngest || shop?.productInventorySource?.latestIngest);

/**
 * The Unify onboarding flow at `/onboarding` is the one place we collect
 * Business Details / GBP / POS. A shop should be there iff:
 *   - it has no current inventory source connected, AND
 *   - it has no current GBP linked, AND
 *   - it has never connected a POS in the past.
 *
 * The "never have" part is what stops a user who once connected and then
 * disconnected from being thrown back into the welcome flow.
 *
 * @param {object} args
 * @param {object} [args.shop] result of `useShop().shop`
 * @param {Array} [args.sources] result of `useInventorySources(shopId).sources`
 * @param {boolean} [args.sourcesLoading] result of `useInventorySources(shopId).isLoading`
 * @returns {boolean}
 */
export function shopNeedsOnboarding({ shop, sources, sourcesLoading }) {
	if (!shop) return false; // pre-load — let the caller wait for the SWR resolution
	// `useInventorySources` doesn't fire until `shop?.id` resolves, so its
	// first render returns `[]` even when the shop has real sources. Defer
	// the decision until the request has actually resolved.
	if (sourcesLoading) return false;
	// Explicit per-shop completion is authoritative. Onboarding is per-shop
	// (a retailer can have finished setup for one location while another still
	// needs it), so completion lives on the shop row, not user-global Clerk
	// metadata. A completed shop is never dragged back into the flow even if
	// the heuristic below would say otherwise — this is what stops the
	// AppLayout ⇄ /:shopId/onboarding redirect loop (NEA-5724).
	if (shop.onboardingComplete === true) return false;
	const hasCurrentSource = Array.isArray(sources) && sources.length > 0;
	return !hasGbpLinked(shop) && !hasCurrentSource && !hasEverIngested(shop);
}

/**
 * Cheap "this shop looks set up" check usable without `useInventorySources`
 * (it reads the inventory-source data the `/api/my-shop` response already
 * supplements onto the shop). Used to pick a sensible default shop for the
 * multi-location redirect so we don't land first on a setup screen — the
 * switcher is always available to reach the onboarding shop anyway.
 */
export function shopLooksOnboarded(shop) {
	return Boolean(shop) && (shop.onboardingComplete === true || hasGbpLinked(shop) || hasEverIngested(shop));
}

/**
 * The shop a multi-location user lands on at `/`: the first that looks set up,
 * else the first shop. Returns `null` for an empty list.
 */
export function pickDefaultShopId(shops) {
	if (!Array.isArray(shops) || shops.length === 0) return null;
	return (shops.find(shopLooksOnboarded) ?? shops[0]).id;
}

/**
 * "Onboarding done but nothing is selling yet" — drives the full-screen
 * "you're ready to go live" hero on the Overview takeover.
 *
 * Returns `false` for both the "haven't loaded yet" and "loaded as empty
 * array" cases. The only thing that flips it `true` is having at least one
 * channel record where every channel is explicitly disabled. Reasoning: the
 * unify channels API always returns the full catalogue (Discovery / Social /
 * Omnichannel / Delivery) with `enabled` flags, so an empty array genuinely
 * means we haven't heard back from the server yet.
 *
 * @param {object} args
 * @param {Array} [args.channels] result of `useChannels(shopId).channels`
 * @returns {boolean}
 */
export function shopHasNoEnabledChannel({ channels }) {
	if (!Array.isArray(channels) || channels.length === 0) return false;
	return !channels.some((c) => c.enabled === true || c.status === 'enabled');
}

/**
 * Plan / billing / trial-aware status — delegates to `useBillingStatus()`
 * which reads through to Stripe via `GET /api/billing/status/{shopId}`
 * (NEA-5710). Same return shape as before, so the four legacy consumers
 * (`LifecycleBanner`, `ExpiredTopBanner` via AppLayout, the Overview
 * add-payment card and the Setup→Grow swap) render unchanged.
 *
 * Returns `null` when the endpoint reports `status: 'no_subscription'` or
 * is still loading — preserves today's "live retailer" fallback (no UX
 * regression). The legacy reads of `shop.trialStartedAt` / `paymentMethodId`
 * / `plan` are gone — Stripe is the source of truth.
 *
 * `daysLeft` / `trialDays` now derive from the real Stripe `trial_end` /
 * `trial_start` (the previous hard-coded 14-day constant goes away — the
 * actual Unify trial is 30 days per Brief §3). When not trialing,
 * `daysLeft` is 0 so banner conditions (`data/banners.json`) that key off
 * `daysLeft > N && !hasPayment` don't misfire.
 *
 * @returns {null | { plan: string, daysIntoTrial: number, daysLeft: number, hasPayment: boolean, expired: boolean, trialDays: number }}
 */
export function useTrialStatus() {
	const { shop } = useShop();
	const { billing } = useBillingStatus(shop?.id);
	if (!billing || billing.status === 'no_subscription') return null;

	const trialStartMs = billing.trialStart ? Date.parse(billing.trialStart) : null;
	const trialEndMs = billing.trialEnd ? Date.parse(billing.trialEnd) : null;
	const trialDays =
		trialStartMs != null && trialEndMs != null
			? Math.max(1, Math.round((trialEndMs - trialStartMs) / MS_PER_DAY))
			: TRIAL_DAYS_FALLBACK;
	const daysLeft = billing.daysLeft ?? 0;
	const daysIntoTrial = Math.max(0, trialDays - daysLeft);

	return {
		plan: billing.plan ?? null,
		daysIntoTrial,
		daysLeft,
		hasPayment: billing.hasPaymentMethod,
		expired: billing.expired,
		trialDays
	};
}
